/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Sheet, SheetContent } from '../ui/sheet';
import { Button } from '../ui/button';
import ChatPanel from './ChatPanel';
import { useChat } from '../../context/ChatContext';
import { ChatCapabilityLinkDto } from '../../api';

interface GlobalChatProps {
  onCapabilityLink?: (link: ChatCapabilityLinkDto) => void;
}

const useIsDesktop = () => {
  const getMatch = () =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false;
  const [isDesktop, setIsDesktop] = useState(getMatch);

  useEffect(() => {
    const handleResize = () => setIsDesktop(getMatch());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isDesktop;
};

const GlobalChat: React.FC<GlobalChatProps> = ({ onCapabilityLink }) => {
  const [open, setOpen] = useState(false);
  const { ensureSession } = useChat();
  const isDesktop = useIsDesktop();
  const [showHighlight, setShowHighlight] = useState(false);

  // Check if user has interacted with chat before
  useEffect(() => {
    const hasUsedChat = localStorage.getItem('hasUsedChat');
    const shouldHighlight = !hasUsedChat;
    console.log('Chat highlight check:', { hasUsedChat, shouldHighlight });
    setShowHighlight(shouldHighlight);
  }, []);
  const panelWidth = 420;
  const panelMinMargin = 32;
  const getPanelHeight = () =>
    typeof window === 'undefined'
      ? 520
      : Math.min(window.innerHeight * 0.7, 720);
  const defaultDesktopPosition = () => {
    if (typeof window === 'undefined') {
      return { x: panelMinMargin, y: panelMinMargin };
    }
    const height = getPanelHeight();
    return {
      x: window.innerWidth - panelWidth - panelMinMargin,
      y: window.innerHeight - height - panelMinMargin,
    };
  };

  const [panelPosition, setPanelPosition] = useState(defaultDesktopPosition);
  const dragStateRef = useRef<{
    offsetX: number;
    offsetY: number;
  } | null>(null);

  // Ensure correct initial position after mount
  useEffect(() => {
    if (isDesktop && typeof window !== 'undefined') {
      const correctPosition = defaultDesktopPosition();
      setPanelPosition(correctPosition);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const height = getPanelHeight();
      setPanelPosition((prev) => ({
        x: Math.min(
          Math.max(panelMinMargin, prev.x),
          window.innerWidth - panelWidth - panelMinMargin
        ),
        y: Math.min(
          Math.max(panelMinMargin, prev.y),
          window.innerHeight - height - panelMinMargin
        ),
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDesktop]);

  const stopDragging = () => {
    dragStateRef.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', stopDragging);
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!isDesktop) return;
    const dragState = dragStateRef.current;
    if (!dragState || typeof window === 'undefined') return;
    const height = getPanelHeight();
    const newX = event.clientX - dragState.offsetX;
    const newY = event.clientY - dragState.offsetY;
    const clampedX = Math.min(
      Math.max(panelMinMargin, newX),
      window.innerWidth - panelWidth - panelMinMargin
    );
    const clampedY = Math.min(
      Math.max(panelMinMargin, newY),
      window.innerHeight - height - panelMinMargin
    );
    setPanelPosition({ x: clampedX, y: clampedY });
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    if (!isDesktop) return;
    event.preventDefault();
    dragStateRef.current = {
      offsetX: event.clientX - panelPosition.x,
      offsetY: event.clientY - panelPosition.y,
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
    };
  }, []);

  useEffect(() => {
    if (open) {
      ensureSession();
    }
  }, [open, ensureSession]);

  const handleOpen = () => {
    setOpen(true);
    ensureSession();
    localStorage.setItem('hasUsedChat', 'true');
    setShowHighlight(false);
  };

  return (
    <>
      <div className="fixed bottom-20 right-4 z-50 md:bottom-8 md:right-8">
        <div className="relative">
          <Button
            className={`harmonic-companion-button h-14 w-14 rounded-full shadow-lg transition-all ${
              showHighlight ? 'animate-pulse-subtle' : ''
            }`}
            onClick={handleOpen}
          >
            <MessageSquare className="h-5 w-5" />
            <span className="sr-only">Open chat</span>
          </Button>
          {showHighlight && (
            <>
              <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-blue-500 ring-2 ring-background animate-ping" />
              <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-blue-500 ring-2 ring-background" />
            </>
          )}
        </div>
      </div>
      {isDesktop ? (
        open && (
          <div
            className="fixed z-50 hidden lg:flex"
            style={{
              left: panelPosition.x,
              top: panelPosition.y,
            }}
          >
            <div className="w-[420px] max-w-[90vw] rounded-lg border bg-background shadow-2xl overflow-hidden flex flex-col">
              <div
                className="flex items-center justify-between border-b bg-muted px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-move flex-shrink-0"
                onPointerDown={handlePointerDown}
              >
                <span>Harmonic Companion</span>
                <span className="text-[10px] font-normal">Drag</span>
              </div>
              <div className="flex flex-col h-[70vh] min-h-[420px] max-h-[720px]">
                <ChatPanel
                  onClose={() => setOpen(false)}
                  onCapabilityLink={onCapabilityLink}
                />
              </div>
            </div>
          </div>
        )
      ) : (
        <Sheet open={open} onOpenChange={setOpen} modal={true}>
          <SheetContent
            side="bottom"
            className="flex h-[65vh] w-full flex-col p-0 sm:max-w-full rounded-t-xl"
            showCloseButton={false}
          >
            <ChatPanel onClose={() => setOpen(false)} onCapabilityLink={onCapabilityLink} />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
};

export default GlobalChat;
